import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';
import { Label, FormGroup, Select } from './ui';
import { presetToCron, cronToDescription, isValidCron } from '../utils/cron-helpers';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const INTERVAL_OPTIONS = [1, 2, 4, 6, 8, 12];
const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const PRESETS = [
  { key: 'daily', icon: '📅', labelKey: 'preset.daily' },
  { key: 'interval', icon: '⏰', labelKey: 'preset.interval' },
  { key: 'weekdays', icon: '📆', labelKey: 'preset.weekdays' },
  { key: 'once', icon: '📌', labelKey: 'preset.once' },
];

const SUGGESTION_KEYS = [
  'suggestion.dailySummary',
  'suggestion.confirmAppointments',
  'suggestion.checkPending',
];

const OBJECTIVE_MAX_LENGTH = 500;

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function getMinDateTimeString() {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildScheduleObject({
  selectedPreset,
  hour,
  intervalHours,
  selectedDays,
  onceDate,
  onceTime,
  agentInstanceId,
  objective,
  showObjective,
  showAgent,
  timezone,
  t,
}) {
  // Base schedule
  const schedule = {
    type: null,
    cronExpression: null,
    scheduledAt: null,
    agentInstanceId: agentInstanceId || '',
    objective: objective || '',
    isValid: false,
  };

  if (!selectedPreset) return schedule;

  if (selectedPreset === 'once') {
    schedule.type = 'one-shot';
    if (onceDate && onceTime) {
      const dt = new Date(`${onceDate}T${onceTime}`);
      schedule.scheduledAt = dt.toISOString();
    }
  } else {
    schedule.type = 'recurring';
    let preset = null;
    if (selectedPreset === 'daily') {
      preset = { type: 'daily', hour };
    } else if (selectedPreset === 'interval') {
      preset = { type: 'interval', hours: intervalHours };
    } else if (selectedPreset === 'weekdays') {
      preset = { type: 'weekdays', days: selectedDays, hour };
    }
    if (preset) {
      schedule.cronExpression = presetToCron(preset, timezone);
    }
  }

  // Validation
  schedule.isValid = validateSchedule(schedule, showObjective, showAgent).length === 0;

  return schedule;
}

function validateSchedule(schedule, showObjective, showAgent = true) {
  const errors = [];

  // Frequency required
  if (!schedule.type) {
    errors.push('frequencyRequired');
    return errors;
  }

  // Objective required when showObjective
  if (showObjective && !schedule.objective.trim()) {
    errors.push('objectiveRequired');
  }

  // Objective max length
  if (schedule.objective.length > OBJECTIVE_MAX_LENGTH) {
    errors.push('objectiveRequired');
  }

  // Agent required (only when agent selection is visible)
  if (showAgent && !schedule.agentInstanceId) {
    errors.push('agentRequired');
  }

  // Recurring validation
  if (schedule.type === 'recurring') {
    if (!schedule.cronExpression) {
      errors.push('frequencyRequired');
    } else if (!isValidCron(schedule.cronExpression)) {
      // Check if it's a rate with < 30 min
      const rateMatch = schedule.cronExpression.match(
        /^rate\((\d+)\s+(minute|minutes)\)$/
      );
      if (rateMatch && parseInt(rateMatch[1], 10) < 30) {
        errors.push('minInterval');
      } else {
        errors.push('invalidExpression');
      }
    }
  }

  // One-shot validation
  if (schedule.type === 'one-shot') {
    if (!schedule.scheduledAt) {
      errors.push('frequencyRequired');
    } else {
      const scheduledTime = new Date(schedule.scheduledAt).getTime();
      const minTime = Date.now() + 5 * 60 * 1000;
      if (scheduledTime < minTime) {
        errors.push('minFutureTime');
      }
    }
  }

  return errors;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function VisualScheduler({
  onScheduleChange,
  agents = [],
  autoSelectAgent = false,
  showObjective = true,
  showAgent = true,
  initialSchedule = null,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  showErrors = false,
}) {
  const { t } = useTranslation('scheduling');

  // State
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [hour, setHour] = useState(8);
  const [intervalHours, setIntervalHours] = useState(2);
  const [selectedDays, setSelectedDays] = useState([]);
  const [onceDate, setOnceDate] = useState('');
  const [onceTime, setOnceTime] = useState('');
  const [objective, setObjective] = useState('');
  const [agentInstanceId, setAgentInstanceId] = useState('');

  // Auto-select agent when autoSelectAgent is true and there's exactly one agent
  useEffect(() => {
    if (autoSelectAgent && agents.length === 1) {
      setAgentInstanceId(agents[0].instanceId);
    }
  }, [autoSelectAgent, agents]);

  // Initialize from initialSchedule if provided
  useEffect(() => {
    if (!initialSchedule) return;
    if (initialSchedule.objective) setObjective(initialSchedule.objective);
    if (initialSchedule.agentInstanceId) setAgentInstanceId(initialSchedule.agentInstanceId);
    // Could parse cronExpression back to preset, but keeping it simple for now
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build schedule object and call onScheduleChange on every state change
  const schedule = useMemo(
    () =>
      buildScheduleObject({
        selectedPreset,
        hour,
        intervalHours,
        selectedDays,
        onceDate,
        onceTime,
        agentInstanceId,
        objective,
        showObjective,
        showAgent,
        timezone,
        t,
      }),
    [selectedPreset, hour, intervalHours, selectedDays, onceDate, onceTime, agentInstanceId, objective, showObjective, showAgent, timezone, t]
  );

  useEffect(() => {
    if (onScheduleChange) {
      onScheduleChange(schedule);
    }
  }, [schedule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validation errors for display
  const errors = validateSchedule(schedule, showObjective, showAgent);

  // Day toggle handler
  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // Suggestion chip handler
  const handleSuggestionClick = (key) => {
    setObjective(t(key));
  };

  // Description preview
  const description =
    schedule.cronExpression ? cronToDescription(schedule.cronExpression, t, timezone) : null;

  return (
    <Container>
      {/* ── Frequency Selection (4.2) ── */}
      <Section>
        <PresetGrid>
          {PRESETS.map((preset) => (
            <PresetCard
              key={preset.key}
              $active={selectedPreset === preset.key}
              onClick={() => setSelectedPreset(preset.key)}
              type="button"
            >
              <PresetIcon>{preset.icon}</PresetIcon>
              <PresetLabel>{t(preset.labelKey)}</PresetLabel>
            </PresetCard>
          ))}
        </PresetGrid>

        {/* Daily options */}
        {selectedPreset === 'daily' && (
          <OptionsRow>
            <FormGroup>
              <Label>{t('selectTime')}</Label>
              <SmallSelect
                value={hour}
                onChange={(e) => setHour(parseInt(e.target.value, 10))}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </option>
                ))}
              </SmallSelect>
            </FormGroup>
          </OptionsRow>
        )}

        {/* Interval options */}
        {selectedPreset === 'interval' && (
          <OptionsRow>
            <FormGroup>
              <Label>{t('selectInterval')}</Label>
              <SmallSelect
                value={intervalHours}
                onChange={(e) => setIntervalHours(parseInt(e.target.value, 10))}
              >
                {INTERVAL_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h === 1 ? t('1hour') : t('nHours', { n: h })}
                  </option>
                ))}
              </SmallSelect>
            </FormGroup>
          </OptionsRow>
        )}

        {/* Weekdays options */}
        {selectedPreset === 'weekdays' && (
          <OptionsRow>
            <FormGroup>
              <Label>{t('selectDays')}</Label>
              <DaysRow>
                {DAYS_OF_WEEK.map((day) => (
                  <DayChip
                    key={day}
                    $active={selectedDays.includes(day)}
                    onClick={() => toggleDay(day)}
                    type="button"
                  >
                    {t(`day.${day}`)}
                  </DayChip>
                ))}
              </DaysRow>
            </FormGroup>
            <FormGroup>
              <Label>{t('selectTime')}</Label>
              <SmallSelect
                value={hour}
                onChange={(e) => setHour(parseInt(e.target.value, 10))}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </option>
                ))}
              </SmallSelect>
            </FormGroup>
          </OptionsRow>
        )}

        {/* Once options */}
        {selectedPreset === 'once' && (
          <OptionsRow>
            <FormGroup>
              <Label>{t('selectDate')}</Label>
              <DateInput
                type="date"
                value={onceDate}
                min={getMinDateTimeString()}
                onChange={(e) => setOnceDate(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('selectTime')}</Label>
              <DateInput
                type="time"
                value={onceTime}
                onChange={(e) => setOnceTime(e.target.value)}
              />
            </FormGroup>
          </OptionsRow>
        )}
      </Section>

      {/* ── Objective Field (4.3) ── */}
      {showObjective && (
        <Section>
          <FormGroup>
            <Label>{t('objectiveLabel')}</Label>
            <ObjectiveInput
              value={objective}
              onChange={(e) => setObjective(e.target.value.slice(0, OBJECTIVE_MAX_LENGTH))}
              placeholder={t('objectivePlaceholder')}
              rows={3}
              maxLength={OBJECTIVE_MAX_LENGTH}
            />
            <CharCount $near={objective.length > OBJECTIVE_MAX_LENGTH * 0.9}>
              {objective.length}/{OBJECTIVE_MAX_LENGTH}
            </CharCount>
          </FormGroup>
          <ChipsRow>
            {SUGGESTION_KEYS.map((key) => (
              <SuggestionChip
                key={key}
                onClick={() => handleSuggestionClick(key)}
                type="button"
              >
                {t(key)}
              </SuggestionChip>
            ))}
          </ChipsRow>
        </Section>
      )}

      {/* ── Agent Selection (4.4) ── */}
      {showAgent && (
      <Section>
        <FormGroup>
          <Label>{t('agentLabel')}</Label>
          {autoSelectAgent && agents.length === 1 ? (
            <AgentAutoSelected>
              {agents[0].icon && <span>{agents[0].icon}</span>}
              <span>{agents[0].name}</span>
            </AgentAutoSelected>
          ) : (
            <Select
              value={agentInstanceId}
              onChange={(e) => setAgentInstanceId(e.target.value)}
            >
              <option value="">{t('selectAgent')}</option>
              {agents.map((agent) => (
                <option key={agent.instanceId} value={agent.instanceId}>
                  {agent.name}
                </option>
              ))}
            </Select>
          )}
        </FormGroup>
      </Section>
      )}

      {/* ── Preview (4.5) ── */}
      {selectedPreset && selectedPreset !== 'once' && schedule.cronExpression && (
        <PreviewSection>
          <PreviewLabel>{t('previewLabel')}</PreviewLabel>
          <PreviewRow>
            <CronRaw>{schedule.cronExpression}</CronRaw>
            {description && <CronDescription>→ {description}</CronDescription>}
          </PreviewRow>
        </PreviewSection>
      )}

      {/* ── Validation Errors (4.6) ── */}
      {errors.length > 0 && (selectedPreset || showErrors) && (
        <ErrorList>
          {errors.map((errKey) => (
            <ErrorItem key={errKey}>{t(`error.${errKey}`)}</ErrorItem>
          ))}
        </ErrorList>
      )}
    </Container>
  );
}

// ─── STYLED COMPONENTS ────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[2]};

  @media (max-width: 639px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PresetCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[2]};
  border: 2px solid ${(p) => (p.$active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${theme.radii.lg};
  background: ${(p) => (p.$active ? theme.colors.primaryLight : theme.colors.white)};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.borderHover)};
    background: ${(p) => (p.$active ? theme.colors.primaryLight : theme.colors.gray50)};
  }
`;

const PresetIcon = styled.span`
  font-size: ${theme.typography.xl};
`;

const PresetLabel = styled.span`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
  text-align: center;
`;

const OptionsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
`;

const SmallSelect = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontFamily};
  background: ${theme.colors.white};
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }
`;

const DaysRow = styled.div`
  display: flex;
  gap: ${theme.spacing[1]};
  flex-wrap: wrap;
`;

const DayChip = styled.button`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border: 1px solid ${(p) => (p.$active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${theme.radii.full};
  background: ${(p) => (p.$active ? theme.colors.primary : theme.colors.white)};
  color: ${(p) => (p.$active ? theme.colors.white : theme.colors.textSecondary)};
  font-size: ${theme.typography.xs};
  font-weight: ${(p) => (p.$active ? theme.typography.semibold : theme.typography.normal)};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const DateInput = styled.input`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontFamily};
  background: ${theme.colors.white};
  outline: none;

  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }
`;

const ObjectiveInput = styled.textarea`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  transition: ${theme.transitions.fast};
  outline: none;
  resize: vertical;
  min-height: 60px;

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
  &:hover {
    border-color: ${theme.colors.borderHover};
  }
  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }
`;

const CharCount = styled.div`
  font-size: ${theme.typography.xs};
  color: ${(p) => (p.$near ? theme.colors.warning : theme.colors.textMuted)};
  text-align: right;
`;

const ChipsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const SuggestionChip = styled.button`
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.full};
  background: ${theme.colors.white};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.xs};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }
`;

const AgentAutoSelected = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
`;

const PreviewSection = styled.div`
  padding: ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
`;

const PreviewLabel = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.medium};
  margin-bottom: ${theme.spacing[2]};
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const CronRaw = styled.code`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  padding: 2px 8px;
  border-radius: ${theme.radii.md};
  font-size: 12px;
  font-family: ${theme.typography.fontMono};
  color: ${theme.colors.primary};
`;

const CronDescription = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.semibold};
`;

const ErrorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const ErrorItem = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
`;
